import { Center } from "../user";
import { useAppSettingsStore } from "../stores/AppSettingsStore";

interface Language {
    code: string;
    name: string;
    englishName: string;
    aiGenerated?: boolean;
}

interface AvailableLanguages {
    [key: string]: Language;
}

// Numeric unit types (UNIT, THOUSAND, MILLION, BILLION, TRILLION)
// NumericUnit.MILLION = 'm'
// NumericUnit.BILLION = 'b'
// NumericUnit.THOUSAND = 'k'
// NumericUnit.TRILLION = 't'

export type NumericUnit = 'k' | 'm' | 'b' | 't';

export function getNumericUnit(amount: number): NumericUnit | undefined {
    let unit: NumericUnit | undefined = undefined;
    if (Math.abs(amount) >= 1_000_000_000_000) {
        unit = 't';
    } else if (Math.abs(amount) >= 1_000_000_000) {
        unit = 'b';
    } else if (Math.abs(amount) >= 1_000_000) {
        unit = 'm';
    } else if (Math.abs(amount) >= 1_000) {
        unit = 'k';
    } else {
        unit = undefined;
    }

    return unit;
}

export function formatCompactCurrency(
    amount: number,
    c: Center,
    unit?: NumericUnit
): string {
    let displayUnit = '';
    let value = amount;

    switch (unit) {
        case 'k':
            value = amount / 1_000;
            displayUnit = 'k';
            break;
        case 'm':
            value = amount / 1_000_000;
            displayUnit = 'M';
            break;
        case 'b':
            value = amount / 1_000_000_000;
            displayUnit = 'B';
            break;
        case 't':
            value = amount / 1_000_000_000_000;
            displayUnit = 'T';
            break;
        default:
            value = amount;
            displayUnit = '';
    }

    // Format le nombre avec max 2 décimales
    const formatted = new Intl.NumberFormat(c.language, {
        style: 'currency',
        minimumFractionDigits: 0,
        currency: c.currencyCode,
    }).format(value);

    // Ajoute l'unité à la fin (ex: 1.2M €)
    return displayUnit ? `${formatted}(${displayUnit})` : formatted;
}



export const availableLanguages: AvailableLanguages = {
    // 'af': { code: 'af', name: 'Afrikaans' },
    // 'ar': { code: 'ar', name: 'العربية' },
    // 'az': { code: 'az', name: 'Azərbaycanca' },
    // 'be': { code: 'be', name: 'Беларуская' },
    // 'bg': { code: 'bg', name: 'Български' },
    // 'bn': { code: 'bn', name: 'বাংলা' },
    // 'bs': { code: 'bs', name: 'Bosanski' },
    // 'ca': { code: 'ca', name: 'Català' },
    // 'cs': { code: 'cs', name: 'Čeština' },
    // 'cy': { code: 'cy', name: 'Cymraeg' },
    // 'da': { code: 'da', name: 'Dansk' },
    // 'de': { code: 'de', name: 'Deutsch' },
    // 'el': { code: 'el', name: 'Ελληνικά' },
    'de': { code: 'de', name: 'Deutsch', englishName: 'German', aiGenerated: true },
    'en': { code: 'en', name: 'English', englishName: 'English' },
    'es': { code: 'es', name: 'Español', englishName: 'Spanish', aiGenerated: true },
    // 'et': { code: 'et', name: 'Eesti' },
    // 'fa': { code: 'fa', name: 'فارسی' },
    // 'fi': { code: 'fi', name: 'Suomi' },
    'fr': { code: 'fr', name: 'Français', englishName: 'French' },
    // 'he': { code: 'he', name: 'עברית' },
    // 'hi': { code: 'hi', name: 'हिन्दी' },
    // 'hr': { code: 'hr', name: 'Hrvatski' },
    // 'hu': { code: 'hu', name: 'Magyar' },
    // 'hy': { code: 'hy', name: 'Հայերեն' },
    // 'id': { code: 'id', name: 'Bahasa Indonesia' },
    // 'is': { code: 'is', name: 'Íslenska' },
    'it': { code: 'it', name: 'Italiano', englishName: 'Italian', aiGenerated: true },
    // 'ja': { code: 'ja', name: '日本語' },
    // 'ka': { code: 'ka', name: 'ქართული' },
    // 'kk': { code: 'kk', name: 'Қазақша' },
    // 'ko': { code: 'ko', name: '한국어' },
    // 'lt': { code: 'lt', name: 'Lietuvių' },
    // 'lv': { code: 'lv', name: 'Latviešu' },
    // 'mk': { code: 'mk', name: 'Македонски' },
    // 'ms': { code: 'ms', name: 'Bahasa Melayu' },
    // 'mt': { code: 'mt', name: 'Malti' },
    // 'nl': { code: 'nl', name: 'Nederlands' },
    // 'no': { code: 'no', name: 'Norsk' },
    // 'pl': { code: 'pl', name: 'Polski' },
    // 'pt': { code: 'pt', name: 'Português' },
    // 'ro': { code: 'ro', name: 'Română' },
    // 'ru': { code: 'ru', name: 'Русский' },
    // 'sk': { code: 'sk', name: 'Slovenčina' },
    // 'sl': { code: 'sl', name: 'Slovenščina' },
    // 'sr': { code: 'sr', name: 'Српски' },
    // 'sv': { code: 'sv', name: 'Svenska' },
    // 'th': { code: 'th', name: 'ไทย' },
    // 'tr': { code: 'tr', name: 'Türkçe' },
    // 'uk': { code: 'uk', name: 'Українська' },
    // 'ur': { code: 'ur', name: 'اردو' },
    // 'vi': { code: 'vi', name: 'Tiếng Việt' },
    // 'zh': { code: 'zh', name: '中文' }
};

export class Lang {
    code: string;
    name: string;
    englishName: string;

    constructor(code: string) {
        this.code = code;

        if (!this.codeIsAvailable()) {
            throw new Error(`Language code '${code}' is not available`);
        }

        const lang = availableLanguages[code];
        this.name = lang.name;
        this.englishName = lang.englishName;
    }


    private codeIsAvailable(): boolean {
        // Check if the language code is valid among the available languages
        return availableLanguages.hasOwnProperty(this.code);
    }
}

const languagesMapping: { [key: string]: string } = {
    'fr': 'fr-FR',
    'us': 'en-US',
    'ca': 'en-CA',
    'cn': 'zh-CN',
    'cz': 'cs-CZ',
    'de': 'de-DE',
    'es': 'es-ES',
    'en': 'en-GB',
    'fi': 'fi-FI',
    'hu': 'hu-HU',
    'it': 'it-IT',
    'jp': 'ja-JP',
    'nl': 'nl-NL',
    'pl': 'pl-PL',
    'pt': 'pt-PT',
    'ro': 'ro-RO',
    'ru': 'ru-RU',
    'se': 'sv-SE',
    'sk': 'sk-SK',
    'tr': 'tr-TR',
}

export function getLanguageCode(langCode?: string): string {
    if (!langCode) {
        return 'en';
    }
    const c = langCode.split('-')[0].toLocaleLowerCase();
    return languagesMapping[c] || navigator.language || 'en';
}

/**
 * Get the number of decimal places for a currency
 * @param currency - ISO 4217 currency code
 * @returns number of decimal places
 */
function getCurrencyDecimalPlaces(currency: string): number {
    // Currencies with 0 decimal places
    const zeroDecimalCurrencies = new Set([
        'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW',
        'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'
    ]);

    // Currencies with 3 decimal places
    const threeDecimalCurrencies = new Set([
        'BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND'
    ]);

    if (zeroDecimalCurrencies.has(currency.toUpperCase())) {
        return 0;
    } else if (threeDecimalCurrencies.has(currency.toUpperCase())) {
        return 3;
    } else {
        return 2; // Default for most currencies (USD, EUR, etc.)
    }
}

/**
 * Format currency with automatic decimal conversion
 * @param amount - Amount in base currency units (e.g., cents for EUR/USD, yen for JPY)
 * @param langCode - Language code
 * @param currency - ISO 4217 currency code
 * @param fromInteger - Whether the amount is an integer that needs decimal conversion (default: false)
 * @returns formatted currency string
 */
export function formatCurrency(amount: number, langCode: string, currency: string, fromInteger: boolean = true): string {
    let convertedAmount = amount;

    if (fromInteger) {
        const decimalPlaces = getCurrencyDecimalPlaces(currency);
        convertedAmount = amount / Math.pow(10, decimalPlaces);
    }

    return new Intl.NumberFormat(getLanguageCode(langCode), {
        style: 'currency',
        currency: currency
    }).format(convertedAmount);
}

// unit: k, m, b, t

/**
 * Options pour le formatage des montants
 */
export interface CurrencyFormatOptions {
    /** Centre pour déterminer devise et langue */
    center?: Center
    /** Forcer une langue spécifique (priorité sur center) */
    language?: string
    /** Forcer une devise spécifique (priorité sur center) */
    currency?: string
    /** Format compact avec unités (k, M, B, T) */
    compact?: boolean
    /** Montant est un entier (diviser par 100 pour EUR) */
    fromInteger?: boolean
    /** Forcer une unité spécifique */
    forceUnit?: NumericUnit
}

/**
 * Fonction centralisée pour formater les montants
 * Gère automatiquement le format compact et les unités
 */
export function formatAmount(amount: number, options: CurrencyFormatOptions = {}): string {
    const {
        center,
        language: forcedLanguage,
        currency: forcedCurrency,
        compact = false,
        fromInteger = true,
        forceUnit
    } = options

    // Déterminer devise et langue avec priorité aux options forcées
    const { currency, language } = getCurrencyAndLanguage(center, forcedLanguage, forcedCurrency)
    
    // Si format compact demandé ou unité forcée
    if (compact || forceUnit) {
        // Convertir d'abord le montant depuis l'entier si nécessaire
        let realAmount = amount
        if (fromInteger) {
            const decimalPlaces = getCurrencyDecimalPlaces(currency)
            realAmount = amount / Math.pow(10, decimalPlaces)
        }

        // Déterminer l'unité sur le montant réel
        const unit = forceUnit || getNumericUnit(realAmount)

        if (unit) {
            // Convertir le montant réel selon l'unité
            const convertedAmount = formatForNumericUnit(realAmount, unit)
            const unitSymbol = getUnitSymbol(unit)

            // Format compact: fewer decimals, unit symbol after number
            // e.g. "40K €" instead of "40,00 €k"
            const locale = getLanguageCode(language)
            const parts = new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: convertedAmount % 1 === 0 ? 0 : 1,
            }).formatToParts(convertedAmount)

            // Insert the unit symbol right after the integer/decimal parts
            const result = parts.map(p => {
                if (p.type === 'integer' || p.type === 'decimal' || p.type === 'fraction') {
                    return p.value
                }
                return p.value
            })

            // Find where the number ends and insert unit symbol there
            let numberEnd = -1
            for (let i = parts.length - 1; i >= 0; i--) {
                if (['integer', 'fraction', 'decimal', 'group'].includes(parts[i].type)) {
                    numberEnd = i
                    break
                }
            }

            if (numberEnd >= 0) {
                const before = parts.slice(0, numberEnd + 1).map(p => p.value).join('')
                const after = parts.slice(numberEnd + 1).map(p => p.value).join('')
                return before + unitSymbol + after
            }

            return result.join('') + unitSymbol
        }
    }
    
    // Format normal
    return formatCurrency(amount, language, currency, fromInteger)
}

/**
 * Fonction de compatibilité - utilise formatAmount en interne
 */
export function formatCurrencyForCenter(amount: number, c?: Center, fromInteger: boolean = true): string {
    return formatAmount(amount, { center: c, fromInteger })
}

/**
 * Détermine la devise et langue selon le centre, options forcées ou paramètres globaux
 * Priorité : options forcées > centre > paramètres globaux
 */
function getCurrencyAndLanguage(
    center?: Center, 
    forcedLanguage?: string, 
    forcedCurrency?: string
): { currency: string; language: string } {
    
    // Déterminer la langue (priorité : forcée > centre > globale)
    let language: string
    if (forcedLanguage) {
        language = forcedLanguage
    } else if (center?.language) {
        language = center.language
    } else {
        const appSettings = useAppSettingsStore()
        language = appSettings.mostUsedLanguage || "FR"
    }
    
    // Déterminer la devise (priorité : forcée > centre > globale)  
    let currency: string
    if (forcedCurrency) {
        currency = forcedCurrency
    } else if (center?.currencyCode) {
        currency = center.currencyCode
    } else {
        const appSettings = useAppSettingsStore()
        currency = appSettings.mostUsedCurrency || "EUR"
    }
    
    return { currency, language }
}

/**
 * Retourne le symbole d'unité approprié pour l'affichage
 */
function getUnitSymbol(unit: NumericUnit): string {
    switch (unit) {
        case 'k':
            return 'k';
        case 'm':
            return 'M';
        case 'b':
            return 'B';
        case 't':
            return 'T';
        default:
            return '';
    }
}

export function formatPercentage(amount: number, c?: Center|string, decimal?: number): string {
    let language = typeof c === 'string' ? c : c?.language;
    if (!decimal) {
        decimal = 2;
    }

    return new Intl.NumberFormat(getLanguageCode(language), { style: 'percent', minimumFractionDigits: decimal }).format(amount);
}

function formatForNumericUnit(amount: number, unit: NumericUnit): number {
    switch (unit) {
        case 'k':
            return amount / 1000;
        case 'm':
            return amount / 1000000;
        case 'b':
            return amount / 1000000000;
        case 't':
            return amount / 1000000000000;
        default:
            return amount;
    }
}